import { CreateStipendStep } from "./types";
import { Body, Superscript } from "@hypercard/web-components";
import { Button } from "@/components/ui/button";
import { CaretRight, Siren, UsersThree } from "@phosphor-icons/react";
import { IconBox } from "@common/components";
import { CSSProperties, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useRecoilState } from "recoil";
import { createOrEditStipendState } from "@common/state/createOrEditStipendState";
import { formatDollars } from "@common/components/utils";
import { AnimatedProps, animated } from "@react-spring/web";
import { getDepartmentDisplay, getFrequencyDisplay } from "../../util";
import { TestStipendRule } from "./TestStipendRule";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { useValidateStipendRuleMutation } from "@common/utils/graphql";
import { endOfDay, startOfDay } from "date-fns";
import { useToast } from "@/components/ui/use-toast";

const SuggestionButton = ({
  onClick,
  text,
  title,
}: {
  onClick: (text: string) => void;
  text: string;
  title: string;
}) => (
  <button
    onClick={() => onClick(text)}
    className="flex items-center justify-between rounded-md bg-[#292929] p-4 text-start text-body-sm font-normal outline-none hover:cursor-pointer"
  >
    {title}
    <CaretRight weight="bold" className="ml-2 size-3 shrink-0" />
  </button>
);

const placheholder = `Write your initial policy in plain English and we will turn it into a comprehensive policy, for example:

Purpose: Explain the purpose of the policy.
Scope: Define who the policy applies to.
Eligibility: Detail the eligibility criteria for receiving the stipend.`;

const suggestionText = [
  {
    title: "Require receipts for anything over a specific amount.",
    text: "A receipt is required for transactions greater than $20.00",
  },
  {
    title: "Require expenses to occur within a certain radius of the office.",
    text: "Only allow transactions made within 3 miles of the office.",
  },
  {
    title: "Available during US workdays between specific time frames.",
    text: "Purchases must be made on Monday-Friday between 10am and 4pm.",
  },
];

type Props = AnimatedProps<{
  onNext: (step: CreateStipendStep) => void;
  onBack: () => void;
  isEditing: boolean;
  style: CSSProperties;
}>;
export function RulePolicy({ onNext, onBack, style, isEditing }: Props) {
  const [
    {
      name,
      departments,
      longDetails,
      category,
      frequency,
      startsAt,
      expiresAt,
      amount,
      terms: savedTerms,
    },
    setCreateOrEditStipendState,
  ] = useRecoilState(createOrEditStipendState);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const message = useRef("");
  const { toast } = useToast();
  const [terms, setTerms] = useState(savedTerms);
  const [isTesting, setIsTesting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validateStipendRule] = useValidateStipendRuleMutation();

  const appendText = (text: string) => {
    setTerms((prev) => (prev ? `${prev}\n\n${text}` : text));
    if (!textareaRef.current) return;

    // Set the value of the textarea before scrolling to the bottom setPolicy
    // is an async function so it might not have updated the value before
    textareaRef.current.value = terms ? `${terms}\n\n${text}` : text;
    textareaRef.current.scroll({
      top: textareaRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const handleNext = async () => {
    setCreateOrEditStipendState((prev) => ({
      ...prev,
      terms,
    }));
    let departmentIds: number[] | null = departments
      .map((d) => d.id)
      .filter((id) => id !== null) as number[];
    if (departmentIds.length === 0) {
      departmentIds = null;
    }
    try {
      setIsLoading(true);
      message.current = "Please add more details to the policy.";
      const { data } = await validateStipendRule({
        variables: {
          input: {
            name,
            terms,
            departmentIds,
            frequency: frequency!,
            amount: Number(amount),
            longDetails,
            category: category!,
            startsAt: startOfDay(startsAt!).toISOString(),
            expiresAt: expiresAt ? endOfDay(expiresAt!).toISOString() : null,
          },
        },
      });
      if (
        data?.validateStipendRule.__typename !==
        "MutationValidateStipendRuleSuccess"
      ) {
        throw new Error();
      }
      if (data.validateStipendRule.data.isValid) {
        onNext(CreateStipendStep.Confirmation);
      } else if (data.validateStipendRule.data.message) {
        message.current = data.validateStipendRule.data.message.replace(
          /'DESCRIPTION'/g,
          "description",
        );
      }
    } catch {
      toast({
        title: "An error occurred while validating the policy.",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <animated.div
      style={style}
      className="mt-6 flex w-full grow flex-col pl-6 lg:pl-10"
    >
      <Body size="2xl" weight="medium">
        {`${isEditing ? "Edit" : "Stipend"} Rules`}
      </Body>
      <Body size="md" weight="medium" className="mt-3 text-white/70">
        Simply write your initial policy in plain English, and we will transform
        it into a comprehensive policy. This policy will serve as the terms and
        conditions of the stipend.
      </Body>
      <div className="mt-10 flex grow flex-col justify-center gap-x-4 lg:max-h-[calc(100vh-360px)] lg:flex-row">
        <div className="flex grow flex-col overflow-hidden rounded-md bg-[#1B1B1B] p-6">
          <Body size="md" weight="medium">
            Instant Edit Options:
          </Body>
          <Body size="xs" weight="regular" className="mt-6 text-white/70">
            Can add details by clicking the options below:
          </Body>
          <div className="mt-4 flex-grow space-y-3 overflow-y-auto scrollbar-thin">
            {suggestionText.map((suggestion) => (
              <SuggestionButton
                key={`suggestion-text--${suggestion}`}
                onClick={appendText}
                text={suggestion.text}
                title={suggestion.title}
              />
            ))}
          </div>
        </div>
        <div className="mt-8 flex h-[500px] grow flex-col rounded-md bg-[#1B1B1B] p-6 lg:mt-0 lg:h-auto lg:w-2/3">
          <div className="flex flex-col items-start rounded-md bg-[#0E0E0E] px-6 py-4 md:flex-row">
            <IconBox className="shrink-0 p-2">
              <UsersThree className="size-6" />
            </IconBox>
            <div className="ml-3 space-y-1">
              <Body size="md" weight="medium">
                {name}
              </Body>
              <Body
                size="sm"
                weight="regular"
                className="text-white/70"
              >{`${getDepartmentDisplay(departments)} · ${getFrequencyDisplay(frequency)} · ${formatDollars(amount)}`}</Body>
            </div>
          </div>
          <Textarea
            ref={textareaRef}
            value={terms}
            placeholder={placheholder}
            onChange={(e) => setTerms(e.target.value)}
            className="mt-6 flex grow flex-col"
          />
        </div>
      </div>
      <div className="mt-8 flex flex-grow items-end justify-between">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-4">
          <Button onClick={() => setIsTesting(true)} className="w-44">
            Test Rules
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button onClick={handleNext} className="w-44">
                Next
              </Button>
            </DialogTrigger>
            <DialogContent
              onOpenAutoFocus={(e: Event) => e.preventDefault()}
              className=" min-w-[536px]"
            >
              <div
                className="tems-center absolute inset-0 flex justify-center"
                style={{ visibility: !isLoading ? "hidden" : "visible" }}
              >
                <FullPageLoader />
              </div>
              <div
                className="flex max-h-[75vh] flex-col overflow-hidden"
                style={{ visibility: isLoading ? "hidden" : "visible" }}
              >
                <DialogHeader className="shrink-0">
                  <Superscript size="md" className="text-white/70">
                    Missing Information
                  </Superscript>
                  <DialogTitle>Policy Missing Information</DialogTitle>
                </DialogHeader>
                <DialogDescription className="shrink-0">
                  We detected that some information you may want is missing.
                </DialogDescription>
                <div className="my-10 flex w-full grow items-start overflow-y-auto rounded-md border border-error bg-[#BE3E221A] p-4 scrollbar-thin">
                  <IconBox className="mr-3">
                    <Siren className="size-6 text-error" />
                  </IconBox>
                  <div className="flex flex-col overflow-y-auto scrollbar-thin">
                    <Body
                      size="sm"
                      weight="medium"
                      className="whitespace-pre-line"
                    >
                      {message.current}
                    </Body>
                  </div>
                </div>
                <DialogFooter className="shrink-0">
                  <DialogClose asChild>
                    <Button type="button" variant="ghost">
                      Edit Policy
                    </Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button
                      type="button"
                      variant="default"
                      onClick={() => onNext(CreateStipendStep.Confirmation)}
                    >
                      Confirm Policy
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Sheet open={isTesting}>
        <SheetContent onClose={() => setIsTesting(false)}>
          {isTesting && (
            <TestStipendRule
              terms={terms}
              onClose={() => setIsTesting(false)}
            />
          )}
        </SheetContent>
      </Sheet>
    </animated.div>
  );
}
